<template>
  <!-- La raíz queda en su lugar; el panel se muda a <body> (ver DashboardWidgetCatalog). -->
  <span class="rws-anchor">
    <div v-show="value" ref="overlay" class="rws" @keydown.esc="close">
      <div class="rws__backdrop" @click="close"></div>
      <div class="rws__panel" role="dialog" aria-modal="true" :aria-label="t('title')">
        <header class="rws__head">
          <div class="rws__titles">
            <h2 class="rws__title">{{ t("title") }}</h2>
            <p class="rws__subtitle">{{ title }}</p>
          </div>
          <button type="button" class="rws__close" :aria-label="t('cancel')" @click="close">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <div class="rws__body">
          <form class="rws__form" @submit.prevent="save">
            <fieldset class="rws__group">
              <legend>{{ t("showAs") }}</legend>
              <div class="rws__seg" role="radiogroup">
                <button
                  v-for="m in modes"
                  :key="m.key"
                  type="button"
                  role="radio"
                  :aria-checked="draft.mode === m.key ? 'true' : 'false'"
                  class="rws__seg-btn"
                  :class="{ 'is-active': draft.mode === m.key }"
                  @click="draft.mode = m.key"
                >
                  <i class="fas" :class="`fa-${m.icon}`" aria-hidden="true"></i> {{ m.label }}
                </button>
              </div>
            </fieldset>

            <template v-if="draft.mode === 'chart'">
              <fieldset class="rws__group">
                <legend>{{ t("chartType") }}</legend>
                <div class="rws__types">
                  <button
                    v-for="c in chartTypes"
                    :key="c.key"
                    type="button"
                    class="rws__type"
                    :class="{ 'is-active': draft.chart.type === c.key }"
                    :aria-pressed="draft.chart.type === c.key ? 'true' : 'false'"
                    @click="draft.chart.type = c.key"
                  >
                    <i class="fas" :class="`fa-${c.icon}`" aria-hidden="true"></i>
                    <span>{{ c.label }}</span>
                  </button>
                </div>
              </fieldset>

              <fieldset class="rws__group">
                <legend>{{ isPie ? t("slices") : t("xAxis") }}</legend>
                <label class="rws__field">
                  <span>{{ t("field") }}</span>
                  <select v-model="draft.chart.x" class="form-select form-select-sm">
                    <option v-for="f in xFields" :key="f.key" :value="f.key">{{ f.label }}{{ f.type === "date" ? ` (${t("date")})` : "" }}</option>
                  </select>
                </label>
                <label v-if="xIsDate" class="rws__field">
                  <span>{{ t("groupDateBy") }}</span>
                  <select v-model="draft.chart.xDate" class="form-select form-select-sm">
                    <option v-for="b in dateBuckets" :key="b" :value="b">{{ t(`bucket.${b}`) }}</option>
                  </select>
                </label>
              </fieldset>

              <fieldset class="rws__group">
                <legend>{{ isPie ? t("sliceSize") : t("yAxis") }}</legend>
                <label class="rws__field">
                  <span>{{ t("measure") }}</span>
                  <select v-model="draft.chart.agg" class="form-select form-select-sm">
                    <option v-for="a in aggregations" :key="a" :value="a" :disabled="a !== 'count' && !numericFields.length">
                      {{ t(`agg.${a}`) }}
                    </option>
                  </select>
                </label>
                <label v-if="draft.chart.agg !== 'count'" class="rws__field">
                  <span>{{ t("ofField") }}</span>
                  <select v-model="draft.chart.y" class="form-select form-select-sm">
                    <option v-for="f in numericFields" :key="f.key" :value="f.key">{{ f.label }}</option>
                  </select>
                </label>
              </fieldset>

              <fieldset class="rws__group rws__group--row">
                <label class="rws__field">
                  <span>{{ xIsDate ? t("periods") : t("maxCategories") }}</span>
                  <select v-model.number="draft.chart.limit" class="form-select form-select-sm">
                    <option v-for="n in limitOptions" :key="n" :value="n">{{ n }}</option>
                  </select>
                </label>
                <label v-if="!xIsDate" class="rws__field">
                  <span>{{ t("order") }}</span>
                  <select v-model="draft.chart.sort" class="form-select form-select-sm">
                    <option value="value">{{ t("sortValue") }}</option>
                    <option value="label">{{ t("sortLabel") }}</option>
                  </select>
                </label>
              </fieldset>
            </template>

            <template v-else-if="draft.mode === 'list'">
              <fieldset class="rws__group">
                <legend>{{ t("rowTitle") }}</legend>
                <select v-model="draft.list.title" class="form-select form-select-sm">
                  <option :value="null">{{ t("auto") }}</option>
                  <option v-for="f in fields" :key="f.key" :value="f.key">{{ f.label }}</option>
                </select>
              </fieldset>
              <fieldset class="rws__group">
                <legend>{{ t("details", { n: MAX_DETAILS }) }}</legend>
                <div class="rws__checks">
                  <label v-for="f in fields" :key="f.key" class="rws__check">
                    <input
                      v-model="draft.list.details"
                      type="checkbox"
                      :value="f.key"
                      :disabled="!draft.list.details.includes(f.key) && draft.list.details.length >= MAX_DETAILS"
                    />
                    <span>{{ f.label }}</span>
                  </label>
                </div>
              </fieldset>
            </template>

            <template v-else>
              <fieldset class="rws__group">
                <legend>{{ t("kpiFields") }}</legend>
                <p v-if="!numericFields.length" class="rws__hint">{{ t("noNumeric") }}</p>
                <div v-else class="rws__checks">
                  <label v-for="f in numericFields" :key="f.key" class="rws__check">
                    <input
                      v-model="draft.kpi.fields"
                      type="checkbox"
                      :value="f.key"
                      :disabled="!draft.kpi.fields.includes(f.key) && draft.kpi.fields.length >= 4"
                    />
                    <span>{{ f.label }}</span>
                  </label>
                </div>
              </fieldset>
            </template>
          </form>

          <aside class="rws__preview" :aria-label="t('preview')">
            <div class="rws__preview-label">{{ t("preview") }}</div>
            <div class="rws__preview-box" :class="{ 'is-chart': draft.mode === 'chart' && !isFlowChart }">
              <slot name="preview" :draft="draft" />
            </div>
          </aside>
        </div>

        <footer class="rws__foot">
          <button type="button" class="btn btn-sm btn-link text-secondary" @click="close">{{ t("cancel") }}</button>
          <button type="button" class="btn btn-sm btn-primary" @click="save">{{ t("save") }}</button>
        </footer>
      </div>
    </div>
  </span>
</template>

<script>
/**
 * Configuración de un widget de reporte: lista (qué columnas), total (qué sumas) o
 * gráfico (tipo, eje X, eje Y, cantidad y orden), con vista previa en vivo. La vista
 * previa la dibuja el widget por slot, con sus mismos datos.
 */
import { CHART_TYPES, DATE_BUCKETS, AGGREGATIONS } from "../widgetData";

const MAX_DETAILS = 4;
const CHART_ICONS = { column: "chart-bar", bar: "align-left", line: "chart-line", area: "chart-area", pie: "chart-pie", donut: "circle-notch" };

export default {
  name: "ReportWidgetSettings",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: "" },
    /** Config actual normalizada (ver ReportWidget.normalizedConfig). */
    config: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    xFields: { type: Array, default: () => [] },
    numericFields: { type: Array, default: () => [] },
  },
  data() {
    return { draft: this.copy(this.config), MAX_DETAILS };
  },
  computed: {
    modes() {
      return [
        { key: "list", icon: "list", label: this.t("modeList") },
        { key: "kpi", icon: "hashtag", label: this.t("modeKpi") },
        { key: "chart", icon: "chart-bar", label: this.t("modeChart") },
      ];
    },
    chartTypes() {
      return CHART_TYPES.map((key) => ({ key, icon: CHART_ICONS[key], label: this.t(`type.${key}`) }));
    },
    dateBuckets() {
      return DATE_BUCKETS;
    },
    aggregations() {
      return AGGREGATIONS;
    },
    isPie() {
      return ["pie", "donut"].includes(this.draft.chart.type);
    },
    isFlowChart() {
      return ["bar", "pie", "donut"].includes(this.draft.chart.type);
    },
    xIsDate() {
      const f = this.xFields.find((x) => x.key === this.draft.chart.x);
      return !!f && f.type === "date";
    },
    limitOptions() {
      return this.isPie ? [3, 4, 5, 6, 8] : [5, 8, 12, 20, 30];
    },
  },
  watch: {
    value(open) {
      document.body.classList.toggle("dwc-open", !!open);
      if (open) this.draft = this.copy(this.config);
    },
    "draft.chart.agg"(agg) {
      if (agg !== "count" && !this.draft.chart.y && this.numericFields[0]) this.draft.chart.y = this.numericFields[0].key;
    },
    "draft.chart.type"() {
      if (!this.limitOptions.includes(this.draft.chart.limit)) this.draft.chart.limit = this.isPie ? 6 : 8;
    },
    draft: {
      deep: true,
      handler(d) {
        this.$emit("draft", d);
      },
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.overlay);
  },
  beforeDestroy() {
    if (this.value) document.body.classList.remove("dwc-open");
    const el = this.$refs.overlay;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    t(key, params) {
      return this.$tn(`dashboardWidgets.settings.${key}`, params);
    },
    copy(c) {
      return JSON.parse(JSON.stringify(c));
    },
    close() {
      this.$emit("input", false);
    },
    save() {
      this.$emit("save", this.copy(this.draft));
      this.close();
    },
  },
};
</script>

<style scoped>
.rws-anchor { display: contents; }
.rws { position: fixed; inset: 0; z-index: 1060; display: flex; align-items: center; justify-content: center; padding: 24px; }
.rws__backdrop { position: absolute; inset: 0; background: rgba(var(--bs-black-rgb), 0.45); }
.rws__panel {
  position: relative;
  width: min(900px, 100%);
  max-height: min(760px, 100%);
  display: flex;
  flex-direction: column;
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 16px;
  box-shadow: var(--dw-shadow-lg);
  overflow: hidden;
}
.rws__head { display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px 10px; border-bottom: 1px solid var(--dw-border); }
.rws__titles { flex: 1; min-width: 0; }
.rws__title { margin: 0; font-size: 1.05rem; font-weight: 700; }
.rws__subtitle { margin: 2px 0 0; font-size: 0.85rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rws__close { border: 0; background: transparent; color: var(--dw-muted); width: 36px; height: 36px; border-radius: 50%; cursor: pointer; }
.rws__close:hover { background: var(--dw-surface-2); color: var(--dw-text); }
.rws__body { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr); }
.rws__form { overflow-y: auto; padding: 14px 20px; display: flex; flex-direction: column; gap: 14px; border-right: 1px solid var(--dw-border); }
.rws__group { border: 0; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.rws__group--row { flex-direction: row; gap: 10px; }
.rws__group--row .rws__field { flex: 1; }
.rws__group legend { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--dw-muted); margin-bottom: 6px; float: none; width: auto; padding: 0; }
.rws__field { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; color: var(--dw-muted); margin: 0; min-width: 0; }
.rws__field .form-select { color: var(--dw-text); background-color: var(--dw-surface-2); border-color: var(--dw-border); }
.rws__group > .form-select { color: var(--dw-text); background-color: var(--dw-surface-2); border-color: var(--dw-border); }
.rws__seg { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; padding: 3px; border-radius: 10px; background: var(--dw-surface-2); }
.rws__seg-btn {
  border: 0;
  background: transparent;
  color: var(--dw-text);
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 0.82rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.rws__seg-btn.is-active { background: var(--bs-primary); color: var(--bs-white); }
.rws__types { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.rws__type {
  border: 1px solid var(--dw-border);
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 10px;
  padding: 8px 4px;
  font-size: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
.rws__type i { font-size: 1.1rem; color: var(--dw-muted); }
.rws__type.is-active { border-color: var(--bs-primary); box-shadow: 0 0 0 1px var(--bs-primary) inset; }
.rws__type.is-active i { color: var(--bs-primary); }
.rws__checks { display: flex; flex-direction: column; gap: 4px; max-height: 260px; overflow-y: auto; }
.rws__check { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; margin: 0; cursor: pointer; }
.rws__check input { accent-color: var(--bs-primary); }
.rws__hint { margin: 0; font-size: 0.82rem; color: var(--dw-muted); }
.rws__preview { padding: 14px 20px; display: flex; flex-direction: column; gap: 8px; background: var(--dw-surface-2); min-height: 0; overflow-y: auto; }
.rws__preview-label { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--dw-muted); }
.rws__preview-box {
  background: var(--dw-surface);
  border: 1px solid var(--dw-border);
  border-radius: 12px;
  padding: 12px;
  min-height: 220px;
  max-height: 460px;
  overflow: auto;
}
.rws__preview-box.is-chart { height: 280px; overflow: hidden; }
.rws__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 10px 20px; border-top: 1px solid var(--dw-border); }

@media (max-width: 767.98px) {
  .rws { padding: 0; align-items: stretch; }
  .rws__panel { width: 100%; max-height: none; height: 100%; border-radius: 0; }
  .rws__body { display: flex; flex-direction: column; overflow-y: auto; }
  .rws__form { overflow: visible; border-right: 0; padding: 12px 16px; }
  .rws__preview { order: -1; overflow: visible; padding: 12px 16px; flex-shrink: 0; }
  .rws__preview-box.is-chart { height: 220px; }
  .rws__head { padding: 14px 16px 8px; }
  .rws__foot { padding: 10px 16px calc(10px + env(safe-area-inset-bottom)); }
}
</style>
